import { Box, Chip } from "@mui/material";
import useColors from "api/hooks/useColors";
import TextButton from "components/buttons/TextButton";
import FilterMenu from "components/filters/FilterMenu";
import SearchInput from "components/filters/SearchInput";
import StyledDataGrid from "components/StyledDataGrid";
import { useState } from "react";
import { TaskStatus } from "utils/constants";

const ScansTableWidget = ({ scans, isLoading }) => {
  const colors = useColors();
  const [appliedFilters, setAppliedFilters] = useState({
    statusFilter: {},
    typeFilter: {},
    searchString: "",
  });

  const handleClearFilters = () => {
    setAppliedFilters({
      statusFilter: {},
      typeFilter: {},
      searchString: "",
    });
  };

  const statusOptions = [...new Set((scans || []).map((s) => s.status))]
    .filter(Boolean)
    .map((status) => ({
      name: status,
      label: status,
      displayName: status,
    }));

  const typeOptions = [...new Set((scans || []).map((s) => s.scan_type))]
    .filter(Boolean)
    .map((type) => ({
      name: type,
      label: type,
      displayName: type,
    }));

  const filteredScans = (() => {
    if (!scans) return [];
    let result = [...scans];

    const selectedStatuses = Object.keys(
      appliedFilters.statusFilter || {}
    ).filter((key) => appliedFilters.statusFilter[key]);
    if (selectedStatuses.length > 0) {
      result = result.filter((s) => selectedStatuses.includes(s.status));
    }

    const selectedTypes = Object.keys(appliedFilters.typeFilter || {}).filter(
      (key) => appliedFilters.typeFilter[key]
    );
    if (selectedTypes.length > 0) {
      result = result.filter((s) => selectedTypes.includes(s.scan_type));
    }

    if (appliedFilters.searchString) {
      const search = appliedFilters.searchString.toLowerCase();
      result = result.filter(
        (s) =>
          s.customer_id.toLowerCase().includes(search) ||
          s.env_id.toLowerCase().includes(search) ||
          s.initiated_by?.toLowerCase().includes(search) ||
          s.description?.toLowerCase().includes(search)
      );
    }

    return result;
  })();

  const scanColumns = [
    {
      field: "status",
      headerName: "Status",
      flex: 0.5,
      renderCell: (params) => {
        let chipColor = colors.grey_500;
        const status = params.value?.toLowerCase();
        if (status === TaskStatus.IN_PROGRESS) chipColor = colors.warning_main;
        else if (status === TaskStatus.FAILED) chipColor = colors.error_main;
        else if (status === TaskStatus.COMPLETED)
          chipColor = colors.success_main;

        return (
          <Chip
            label={params.value}
            size="small"
            sx={{
              backgroundColor: chipColor,
              color: colors.common_white,
            }}
          />
        );
      },
    },
    { field: "customer_id", headerName: "Customer", flex: 1 },
    { field: "env_id", headerName: "Env", flex: 0.8 },
    { field: "scan_type", headerName: "Type", flex: 0.5 },
    {
      field: "started_at",
      headerName: "Started",
      flex: 1,
      renderCell: (params) => {
        const date = new Date(params.value);
        return date.toLocaleString();
      },
    },
    {
      field: "ended_at",
      headerName: "Ended",
      flex: 1,
      renderCell: (params) => {
        if (!params.value) return "-";
        const date = new Date(params.value);
        return date.toLocaleString();
      },
    },
    { field: "initiated_by", headerName: "Initiated By", flex: 1 },
    { field: "description", headerName: "Description", flex: 1.5 },
  ];

  return (
    <Box
      sx={{ height: "100%", display: "flex", flexDirection: "column", gap: 2 }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 1.5,
          alignItems: "center",
          padding: "8px 0",
          borderBottom: `1px solid ${colors.divider2}`,
        }}
      >
        <Box sx={{ minWidth: "160px" }}>
          <FilterMenu
            filterName="Status"
            fieldName="statusFilter"
            options={statusOptions}
            appliedFilters={appliedFilters}
            setAppliedFilters={setAppliedFilters}
          />
        </Box>
        <Box sx={{ minWidth: "160px" }}>
          <FilterMenu
            filterName="Type"
            fieldName="typeFilter"
            options={typeOptions}
            appliedFilters={appliedFilters}
            setAppliedFilters={setAppliedFilters}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <SearchInput
            fieldName="searchString"
            appliedFilters={appliedFilters}
            setAppliedFilters={setAppliedFilters}
            placeholder="Search scans..."
          />
        </Box>
        <Box>
          <TextButton onClick={handleClearFilters} size="small">
            Clear
          </TextButton>
        </Box>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        <StyledDataGrid
          rows={filteredScans.map((scan, idx) => ({ id: idx, ...scan }))}
          columns={scanColumns}
          loading={isLoading}
          disableRowSelectionOnClick
          pageSizeOptionsOverride={[10, 25, 50]}
          autoHeight={false}
          boxSXProps={{ height: "100%" }}
        />
      </Box>
    </Box>
  );
};

export default ScansTableWidget;
